// Where a custom field is used, shown on the field's own editor.
//
// WHY THIS IS A LINK AND NOT AN EDITOR. Conditional fields are configured on the FORM
// (see ../../pages/admin/form-conditions-edit.js), because a condition is "on this form,
// when Category is X, also ask Y" — the same field is a parent on one form, a child on
// another and neither elsewhere, so there is nothing here to own. What the field screen
// owes its admin is the ANSWER to "is this one hidden until something else is answered,
// and where do I change that?", and a way to get there in one click.
//
// It earns its place for a second reason. The dangerous edit on this screen is changing
// a dropdown's CHOICES: a condition written against a choice that no longer exists fires
// never, shows nothing, and reports nothing. "Decides 3 conditions" sitting beside the
// choice list is the warning that the two are connected.

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';

export default function FieldFormUsage({ fieldId }) {
  const [forms, setForms] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setForms(null);
    setError(null);
    apiFetch(`/admin/custom-fields/${encodeURIComponent(fieldId)}/forms`,
      { signal: controller.signal })
      .then((body) => setForms(body.data))
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [fieldId]);

  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Where this field is used</h2>

      {forms === null && <p className="mt-2 text-sm text-ink-muted">{error ?? 'Loading…'}</p>}

      {forms?.length === 0 && (
        <p className="mt-2 text-sm text-ink-muted">
          No form asks for this field yet, so nothing collects it. Add it to a form from
          that form&rsquo;s own screen.
        </p>
      )}

      {forms?.length > 0 && (
        <>
          <ul className="mt-2 space-y-1">
            {forms.map((form) => (
              <li key={form.id} className="flex flex-wrap items-baseline gap-2 text-sm">
                <Link className="text-ink hover:underline" to={`/admin/ticket-forms/${form.id}`}>
                  {form.name}
                </Link>
                {!form.is_active && <span className="text-xs text-ink-muted">(retired)</span>}
                {form.decides > 0 && (
                  <span className="text-xs text-ink-secondary">
                    decides {form.decides === 1 ? '1 condition' : `${form.decides} conditions`}
                  </span>
                )}
                {form.revealed_by > 0 && (
                  <span className="text-xs text-ink-secondary">
                    hidden until {form.revealed_by === 1 ? 'a condition is' : 'one of '
                      + `${form.revealed_by} conditions is`} met
                  </span>
                )}
              </li>
            ))}
          </ul>

          <p className="mt-2 text-xs text-ink-secondary">
            Conditions are set on the form, not here, because the same field can be the
            deciding one on one form and a hidden one on another. Open a form above to
            change them.
          </p>
          {forms.some((f) => f.decides > 0) && (
            // The quiet failure this screen can cause, said where the choices are edited.
            <p className="mt-1 text-xs text-ink-secondary">
              Renaming or removing a choice above can leave a condition pointing at an
              answer that no longer exists — it then never fires, and the fields it
              reveals stop being asked for.
            </p>
          )}
        </>
      )}
    </section>
  );
}
